/**
 * Where somebody goes once they have an account, and how they get there.
 *
 * A guard puts the address the visitor arrived on in router state (`from`); sign-up and
 * confirmation pass it along untouched, and the sign-in at the end of the trip honours it. The
 * ad funnel rests on this: an ad links to /new/idea?idea=<sentence>, the journey now needs an
 * account, and the sentence the ad promised has to be in the field when the visitor comes back.
 *
 * Router state is written only by this site's own script, but it is still read as untrusted:
 * a returnTo that could name another origin would make the sign-in page an open redirect,
 * signing somebody in on the real builder and then handing them to a look-alike. So only a
 * path on this origin is ever honoured, judged by the browser's own URL parser rather than by
 * string checks — which is what catches `/\evil.example` and a tab inside `//`, both of which
 * the parser turns into another host.
 */

/** A same-origin path (with its query and fragment), or null for anything else. */
export function safeReturnTo(value: unknown): string | null {
  // A path, never a URL: only a leading slash that is not the start of `//host`.
  if (typeof value !== 'string' || !value.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  return url.pathname + url.search + url.hash;
}

/**
 * A query string without `idea`, and nothing else touched.
 *
 * The same rule as the idea screen's (screens/new/Idea.tsx): the idea is the visitor's own
 * words and every PageView carries the full URL, so it comes out; `fbclid` and `utm_*` are the
 * ad attribution the pixel exists for, and they stay.
 */
export function withoutPrompt(search: string): string {
  const params = new URLSearchParams(search);
  // A redeem code too (/redeem?code=…): it waits in state with the rest of the address, never in
  // the sign-up page's own, where the pixel's PageView would carry it.
  if (!params.has('idea') && !params.has('code')) return search;
  params.delete('idea');
  params.delete('code');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * The last hop, as a navigation: where to go, and the idea to carry in state.
 *
 * Carried in state and not in the address because by the time this runs the Meta pixel is
 * usually live — the visitor accepted, and confirmation fired CompleteRegistration — and
 * fbevents sends a PageView with the full URL on every navigation. Arriving FROM an ad, the
 * idea never reached the pixel only because ConsentBanner holds the first PageView until the
 * idea screen has taken it out of the address; an in-app navigation has no such hold.
 */
export function returnTrip(from: unknown): { to: string; state?: { idea: string } } {
  const safe = safeReturnTo(from);
  if (!safe) return { to: '/' };
  const url = new URL(safe, window.location.origin);
  const idea = url.searchParams.get('idea');
  if (idea === null) return { to: safe };
  return { to: url.pathname + withoutPrompt(url.search) + url.hash, state: { idea } };
}
